import { useState, useEffect, useCallback } from 'react';
import { 
  Receipt, Plus, Search, Filter, Trash2, Pencil, Loader2, DollarSign, 
  Calendar, CreditCard, Banknote, ArrowRight, Tag, AlertCircle, FileText, CheckCircle2, X 
} from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { gastosService, Gasto, CategoriaGasto } from '@/services/gastos.service';
import { useApiError } from '@/hooks/useApiError';
import { ApiErrorDisplay } from '@/components/ApiErrorDisplay';
import { dialogs } from '@/stores/dialog-store';
import { toast } from '@/stores/toast-store';

export function ExpensesPage() {
  const { user } = useAuthStore();
  const [expenses, setExpenses] = useState<Gasto[]>([]);
  const [categories, setCategories] = useState<CategoriaGasto[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('');
  const [showNewExpenseModal, setShowNewExpenseModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Gasto | null>(null);
  const [showNewCategoryModal, setShowNewCategoryModal] = useState(false);
  const { error, errorType, handleApiError, handleAuthRedirect } = useApiError();

  const loadData = useCallback(async () => {
    const companyId = user?.company?.id || localStorage.getItem('COMPANY_ID');
    if (!companyId) return;

    setLoading(true);
    try {
      const [expenseList, categoryList] = await Promise.all([
        gastosService.findAll(companyId),
        gastosService.getCategories(companyId),
      ]);
      setExpenses(expenseList || []);
      setCategories(categoryList || []);
    } catch (err: any) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  }, [user, handleApiError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDeleteExpense = async (id: string, concept: string) => {
    if (!(await dialogs.confirm(`¿Estás seguro de eliminar el gasto por "${concept}"?`))) return;

    try {
      await gastosService.remove(id);
      toast.success('Gasto eliminado correctamente.');
      loadData();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Error al eliminar el gasto.');
    }
  };

  // Filtering
  const filteredExpenses = expenses.filter((e) => {
    const concept = (e.notes || e.reference || e.category?.name || '').toLowerCase();
    const searchMatch = !search || concept.includes(search.toLowerCase());
    const categoryMatch = !selectedCategory || e.categoryId === selectedCategory;
    const paymentMatch = !paymentMethodFilter || e.paymentMethod?.toUpperCase() === paymentMethodFilter.toUpperCase();
    return searchMatch && categoryMatch && paymentMatch;
  });

  // KPI Calculations
  const totalAmount = filteredExpenses.reduce((sum, e) => sum + Number(e.amount || 0), 0);
  const cashAmount = filteredExpenses
    .filter((e) => ['CASH', 'EFECTIVO'].includes((e.paymentMethod || '').toUpperCase()))
    .reduce((sum, e) => sum + Number(e.amount || 0), 0);
  const cardTransferAmount = totalAmount - cashAmount;

  return (
    <div className="w-full space-y-6">
      <ApiErrorDisplay error={error} errorType={errorType} onRetry={loadData} onLogin={handleAuthRedirect} />

      {!error && (
        <>
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Receipt className="w-7 h-7 text-blue-600" />
                Gestión de Gastos y Egresos
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Registra y controla los gastos operativos, servicios, viáticos y salidas de caja.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowNewCategoryModal(true)}
                className="px-3.5 py-2 rounded-xl text-sm font-medium border border-gray-300 hover:bg-gray-50 flex items-center gap-1.5 transition-colors"
              >
                <Tag size={16} className="text-gray-600" />
                Nueva Categoría
              </button>
              <button
                onClick={() => setShowNewExpenseModal(true)}
                className="ios-button flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 shadow-sm font-semibold transition-all"
              >
                <Plus size={18} />
                Nuevo Gasto
              </button>
            </div>
          </div>

          {/* Metric KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Total Gastos</p>
                <h3 className="text-2xl font-bold text-gray-900 mt-1">${totalAmount.toFixed(2)}</h3>
                <p className="text-xs text-gray-400 mt-1">{filteredExpenses.length} registros</p>
              </div>
              <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center">
                <DollarSign size={24} />
              </div>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">En Efectivo (Caja)</p>
                <h3 className="text-2xl font-bold text-green-700 mt-1">${cashAmount.toFixed(2)}</h3>
                <p className="text-xs text-gray-400 mt-1">Descontado de corte</p>
              </div>
              <div className="w-12 h-12 bg-green-50 text-green-600 rounded-2xl flex items-center justify-center">
                <Banknote size={24} />
              </div>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Bancos / Tarjeta</p>
                <h3 className="text-2xl font-bold text-purple-700 mt-1">${cardTransferAmount.toFixed(2)}</h3>
                <p className="text-xs text-gray-400 mt-1">Transferencias / Tarjetas</p>
              </div>
              <div className="w-12 h-12 bg-purple-50 text-purple-600 rounded-2xl flex items-center justify-center">
                <CreditCard size={24} />
              </div>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Categorías Activas</p>
                <h3 className="text-2xl font-bold text-gray-900 mt-1">{categories.length}</h3>
                <p className="text-xs text-gray-400 mt-1">Clasificaciones de gasto</p>
              </div>
              <div className="w-12 h-12 bg-amber-50 text-amber-600 rounded-2xl flex items-center justify-center">
                <Tag size={24} />
              </div>
            </div>
          </div>

          {/* Search & Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por concepto, folio o referencia..."
                className="ios-input w-full pl-10 h-10 text-sm"
              />
            </div>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="ios-input w-full sm:w-52 h-10 text-sm"
            >
              <option value="">Todas las Categorías</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <select
              value={paymentMethodFilter}
              onChange={(e) => setPaymentMethodFilter(e.target.value)}
              className="ios-input w-full sm:w-48 h-10 text-sm"
            >
              <option value="">Todos los Métodos</option>
              <option value="CASH">Efectivo</option>
              <option value="CARD">Tarjeta</option>
              <option value="TRANSFER">Transferencia</option>
            </select>
          </div>

          {/* Expenses Table */}
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <Loader2 size={28} className="animate-spin text-blue-600" />
            </div>
          ) : filteredExpenses.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center text-gray-400">
              <Receipt size={48} className="mx-auto text-gray-300 mb-3" />
              <h3 className="text-lg font-semibold text-gray-700 mb-1">No hay gastos registrados</h3>
              <p className="text-sm text-gray-500 mb-4">
                {search || selectedCategory || paymentMethodFilter
                  ? 'No se encontraron gastos con los filtros aplicados'
                  : 'Registra tu primer gasto operativo'}
              </p>
              {!search && !selectedCategory && !paymentMethodFilter && (
                <button
                  onClick={() => setShowNewExpenseModal(true)}
                  className="ios-button inline-flex items-center gap-2"
                >
                  <Plus size={16} /> Registrar Gasto
                </button>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50/70 text-gray-500 text-xs uppercase tracking-wider font-semibold">
                      <th className="px-5 py-3 text-left">Fecha</th>
                      <th className="px-5 py-3 text-left">Categoría</th>
                      <th className="px-5 py-3 text-left">Concepto / Notas</th>
                      <th className="px-5 py-3 text-left">Referencia</th>
                      <th className="px-5 py-3 text-left">Método de Pago</th>
                      <th className="px-5 py-3 text-right">Monto</th>
                      <th className="px-5 py-3 text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredExpenses.map((expense) => {
                      const method = (expense.paymentMethod || 'CASH').toUpperCase();
                      const isCash = ['CASH', 'EFECTIVO'].includes(method);
                      const isCard = ['CARD', 'TARJETA'].includes(method);

                      return (
                        <tr key={expense.id} className="hover:bg-gray-50/50 transition-colors">
                          <td className="px-5 py-3.5 whitespace-nowrap text-xs text-gray-500">
                            {expense.expenseDate || expense.createdAt
                              ? new Date(expense.expenseDate || expense.createdAt || '').toLocaleString('es-MX', {
                                  dateStyle: 'medium',
                                  timeStyle: 'short',
                                })
                              : '—'}
                          </td>
                          <td className="px-5 py-3.5 whitespace-nowrap">
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700">
                              <Tag size={12} />
                              {expense.category?.name || 'General'}
                            </span>
                          </td>
                          <td className="px-5 py-3.5">
                            <span className="font-semibold text-gray-900 block">{expense.notes || 'Gasto Operativo'}</span>
                          </td>
                          <td className="px-5 py-3.5 text-xs text-gray-500">
                            {expense.reference ? (
                              <code className="bg-gray-100 px-2 py-0.5 rounded text-gray-700 font-mono">
                                {expense.reference}
                              </code>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className="px-5 py-3.5 whitespace-nowrap text-xs">
                            <span
                              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full font-medium ${
                                isCash
                                  ? 'bg-green-100 text-green-800'
                                  : isCard
                                  ? 'bg-purple-100 text-purple-800'
                                  : 'bg-blue-100 text-blue-800'
                              }`}
                            >
                              {isCash ? (
                                <Banknote size={12} />
                              ) : isCard ? (
                                <CreditCard size={12} />
                              ) : (
                                <ArrowRight size={12} />
                              )}
                              {isCash ? 'Efectivo' : isCard ? 'Tarjeta' : 'Transferencia'}
                            </span>
                          </td>
                          <td className="px-5 py-3.5 text-right font-bold text-gray-900 text-base">
                            ${Number(expense.amount || 0).toFixed(2)}
                          </td>
                          <td className="px-5 py-3.5 text-right whitespace-nowrap">
                            <button
                              onClick={() => setEditingExpense(expense)}
                              className="text-gray-400 hover:text-blue-600 p-1.5 rounded-lg hover:bg-blue-50 transition-colors inline-flex"
                              title="Editar Gasto"
                            >
                              <Pencil size={16} />
                            </button>
                            <button
                              onClick={() => handleDeleteExpense(expense.id, expense.notes || 'este gasto')}
                              className="text-gray-400 hover:text-red-600 p-1.5 rounded-lg hover:bg-red-50 transition-colors inline-flex"
                              title="Eliminar Gasto"
                            >
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Modal Registrar Nuevo Gasto */}
          {showNewExpenseModal && (
            <NewExpenseModal
              categories={categories}
              onClose={() => setShowNewExpenseModal(false)}
              onSuccess={() => {
                setShowNewExpenseModal(false);
                loadData();
              }}
            />
          )}

          {editingExpense && (
            <NewExpenseModal
              categories={categories}
              expense={editingExpense}
              onClose={() => setEditingExpense(null)}
              onSuccess={() => {
                setEditingExpense(null);
                loadData();
              }}
            />
          )}

          {/* Modal Nueva Categoría */}
          {showNewCategoryModal && (
            <NewCategoryModal
              onClose={() => setShowNewCategoryModal(false)}
              onSuccess={() => {
                setShowNewCategoryModal(false);
                loadData();
              }}
            />
          )}
        </>
      )}
    </div>
  );
}

// =========================================================
// MODAL PARA REGISTRAR NUEVO GASTO
// =========================================================
function NewExpenseModal({
  categories,
  expense,
  onClose,
  onSuccess,
}: {
  categories: CategoriaGasto[];
  expense?: Gasto;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { user } = useAuthStore();
  const [categoryId, setCategoryId] = useState(expense?.categoryId || categories[0]?.id || '');
  const [amount, setAmount] = useState(expense ? String(expense.amount) : '');
  const [paymentMethod, setPaymentMethod] = useState(expense?.paymentMethod || 'CASH');
  const [reference, setReference] = useState(expense?.reference || '');
  const [notes, setNotes] = useState(expense?.notes || '');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const companyId = user?.company?.id || localStorage.getItem('COMPANY_ID');
    const branchId = user?.branch?.id || localStorage.getItem('BRANCH_ID');
    const cashRegisterId = localStorage.getItem('ACTIVE_CASH_REGISTER_ID') || undefined;

    if (!companyId || !categoryId || !amount || Number(amount) <= 0) {
      toast.error('Ingresa un monto válido y selecciona una categoría.');
      return;
    }

    setIsSubmitting(true);
    try {
      if (expense) {
        await gastosService.update(expense.id, {
          categoryId,
          amount: Number(amount),
          paymentMethod,
          reference: reference.trim() || undefined,
          notes: notes.trim() || undefined,
        });
      } else {
        await gastosService.create({
          companyId,
          branchId: branchId || undefined,
          cashRegisterId,
          categoryId,
          amount: Number(amount),
          paymentMethod,
          reference: reference.trim() || undefined,
          notes: notes.trim() || undefined,
          createdById: user?.id || undefined,
        });
      }

      toast.success(expense ? 'Gasto actualizado exitosamente.' : '¡Gasto registrado exitosamente!');
      onSuccess();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Error al guardar el gasto.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-[20px] shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
          <div className="flex items-center gap-2 text-gray-900 font-semibold text-lg">
            <Receipt className="text-blue-600" size={22} />
            <span>{expense ? 'Editar Gasto' : 'Registrar Nuevo Gasto'}</span>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-200">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
              Categoría de Gasto *
            </label>
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              className="ios-input w-full"
              required
            >
              {categories.length === 0 && <option value="">-- Sin categorías disponibles --</option>}
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
              Monto ($ MXN) *
            </label>
            <div className="relative">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 font-bold">$</span>
              <input
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="ios-input w-full pl-8 font-bold text-lg"
                required
                autoFocus
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
              Método de Pago
            </label>
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => setPaymentMethod('CASH')}
                className={`p-2.5 rounded-xl border text-xs font-semibold flex items-center justify-center gap-1.5 transition-all ${
                  paymentMethod === 'CASH'
                    ? 'border-green-600 bg-green-50 text-green-700'
                    : 'border-gray-200 hover:bg-gray-50 text-gray-600'
                }`}
              >
                <Banknote size={16} /> Efectivo
              </button>
              <button
                type="button"
                onClick={() => setPaymentMethod('CARD')}
                className={`p-2.5 rounded-xl border text-xs font-semibold flex items-center justify-center gap-1.5 transition-all ${
                  paymentMethod === 'CARD'
                    ? 'border-purple-600 bg-purple-50 text-purple-700'
                    : 'border-gray-200 hover:bg-gray-50 text-gray-600'
                }`}
              >
                <CreditCard size={16} /> Tarjeta
              </button>
              <button
                type="button"
                onClick={() => setPaymentMethod('TRANSFER')}
                className={`p-2.5 rounded-xl border text-xs font-semibold flex items-center justify-center gap-1.5 transition-all ${
                  paymentMethod === 'TRANSFER'
                    ? 'border-blue-600 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:bg-gray-50 text-gray-600'
                }`}
              >
                <ArrowRight size={16} /> Transf.
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
              Concepto / Notas *
            </label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ej. Pago de luz, compras de papelería, comida chofer..."
              className="ios-input w-full"
              required
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
              Referencia / Folio Ticket (Opcional)
            </label>
            <input
              type="text"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Ej. FAC-1234, TKT-8829"
              className="ios-input w-full"
            />
          </div>

          <div className="pt-4 border-t border-gray-200 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-xl border border-gray-300 text-sm font-medium hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !amount || Number(amount) <= 0}
              className="flex-1 px-4 py-2.5 rounded-xl bg-blue-600 text-white font-semibold text-sm hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={18} className="animate-spin" /> Guardando...
                </>
              ) : (
                expense ? 'Actualizar Gasto' : 'Guardar Gasto'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// =========================================================
// MODAL PARA CREAR NUEVA CATEGORÍA DE GASTO
// =========================================================
function NewCategoryModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const { user } = useAuthStore();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const companyId = user?.company?.id || localStorage.getItem('COMPANY_ID');
    if (!companyId || !name.trim()) return;

    setIsSubmitting(true);
    try {
      await gastosService.createCategory({
        companyId,
        name: name.trim(),
        description: description.trim() || undefined,
      });

      toast.success('Categoría de gasto creada correctamente.');
      onSuccess();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Error al crear la categoría.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-[20px] shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
          <div className="flex items-center gap-2 text-gray-900 font-semibold text-lg">
            <Tag className="text-blue-600" size={20} />
            <span>Nueva Categoría de Gasto</span>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-200">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
              Nombre de la Categoría *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej. Servicios Básicos, Mantenimiento, Viáticos..."
              className="ios-input w-full"
              required
              autoFocus
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
              Descripción (Opcional)
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Breve descripción del tipo de gasto..."
              className="ios-input w-full"
            />
          </div>

          <div className="pt-4 border-t border-gray-200 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-xl border border-gray-300 text-sm font-medium hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !name.trim()}
              className="flex-1 px-4 py-2.5 rounded-xl bg-blue-600 text-white font-semibold text-sm hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={18} className="animate-spin" /> Guardando...
                </>
              ) : (
                'Crear Categoría'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
